/**
 * 根據卡片尺寸回傳基礎大小類別
 * @param {string} size - 卡片尺寸 (xs, sm, md, lg, xl, xxl)
 */
export const getSizeClasses = (size) => {
    const sizeMap = {
        xs: "p-1 text-xs",
        sm: "p-4 text-sm",
        md: "p-6 text-base",
        lg: "p-8 text-lg",
        xl: "p-10 text-xl",
        xxl: "p-12 text-2xl",
    }
    return sizeMap[size]
}

/**
 * 根據卡片尺寸回傳縮小的內容內距類別
 * @param {string} size - 卡片尺寸
 */
export const getContentPaddingClasses = (size) => {
    const paddingMap = {
        xs: "p-1",
        sm: "p-2.5",
        md: "p-3",
        lg: "p-4",
        xl: "p-5",
        xxl: "p-6",
    }
    return paddingMap[size]
}

/**
 * 根據卡片尺寸回傳圖標大小類別
 * @param {string} size - 卡片尺寸
 */
export const getIconSizeClasses = (size) => {
    const iconSizeMap = {
        xs: "w-4 h-4",
        sm: "w-5 h-5",
        md: "w-6 h-6",
        lg: "w-7 h-7",
        xl: "w-8 h-8",
        xxl: "w-10 h-10",
    }
    return iconSizeMap[size]
}

/**
 * 根據卡片尺寸回傳間距類別
 * @param {string} size - 卡片尺寸
 * @returns {{ mb: string, mt: string, gap: string }} - 下邊距、上邊距、gap 類別
 */
export const getSpacingClasses = (size) => {
    const spacingMap = {
        xs: { mb: "mb-0", mt: "mt-0", gap: "gap-1.5" },
        sm: { mb: "mb-1.5", mt: "mt-0.5", gap: "gap-2" },
        md: { mb: "mb-2", mt: "mt-1", gap: "gap-2.5" },
        lg: { mb: "mb-2.5", mt: "mt-1", gap: "gap-3" },
        xl: { mb: "mb-3", mt: "mt-1.5", gap: "gap-3.5" },
        xxl: { mb: "mb-4", mt: "mt-2", gap: "gap-4" },
    }
    return spacingMap[size]
}

/**
 * 回傳數字文字大小類別
 * @param {string} size - 卡片尺寸
 * @param {string} layout - 版型模式 ("single" | "horizontal")
 */
export const getNumberSizeClasses = (size, layout) => {
    if (layout === "single") {
        const sizeMap = {
            xs: "text-md",
            sm: "text-3xl",
            md: "text-4xl",
            lg: "text-5xl",
            xl: "text-6xl",
            xxl: "text-7xl",
        }
        return sizeMap[size]
    } else {
        const sizeMap = {
            xs: "text-md",
            sm: "text-xl",
            md: "text-2xl md:text-3xl",
            lg: "text-3xl md:text-4xl",
            xl: "text-4xl md:text-5xl",
            xxl: "text-5xl md:text-6xl",
        }
        return sizeMap[size]
    }
}

/**
 * 回傳對齊樣式類別
 * @param {string} align - 對齊方式 ("left" | "center" | "right")
 */
export const getAlignClasses = (align) => {
    const alignMap = {
        left: "text-left items-start",
        center: "text-center items-center",
        right: "text-right items-end",
    }
    return alignMap[align]
}

/**
 * 回傳容器最大寬度類別
 * @param {string} size - 卡片尺寸
 * @param {string} layout - 版型模式 ("single" | "horizontal")
 */
export const getContainerWidthClasses = (size, layout) => {
    if (layout === "single") {
        const sizeMap = {
            xs: "max-w-xs",
            sm: "max-w-sm",
            md: "max-w-md",
            lg: "max-w-lg",
            xl: "max-w-xl",
            xxl: "max-w-2xl",
        }
        return sizeMap[size]
    }
    return "w-full"
}

/**
 * 回傳格線系統的 Tailwind 類別
 * @param {number} columnsPerRow - 預設每列幾欄
 * @param {object} [responsiveColumns] - 響應式欄數設定
 */
export const getGridClasses = (
    columnsPerRow = 3,
    responsiveColumns
) => {
    const getColumnClass = (columns) => {
        const columnMap = {
            1: "grid-cols-1",
            2: "grid-cols-2",
            3: "grid-cols-3",
            4: "grid-cols-4",
            5: "grid-cols-5",
            6: "grid-cols-6",
        }
        return columnMap[columns]
    }

    let classes = `grid ${getColumnClass(columnsPerRow)} gap-4`

    if (responsiveColumns) {
        if (responsiveColumns.xs) classes += ` xs:${getColumnClass(responsiveColumns.xs)}`
        if (responsiveColumns.sm) classes += ` sm:${getColumnClass(responsiveColumns.sm)}`
        if (responsiveColumns.md) classes += ` md:${getColumnClass(responsiveColumns.md)}`
        if (responsiveColumns.lg) classes += ` lg:${getColumnClass(responsiveColumns.lg)}`
        if (responsiveColumns.xl) classes += ` xl:${getColumnClass(responsiveColumns.xl)}`
    }

    return classes
}

/**
 * 回傳帶分隔線的格線系統類別
 * @param {number} columnsPerRow - 預設每列幾欄
 * @param {object} [responsiveColumns] - 響應式欄數設定
 */
export const getConnectedGridClasses = (
    columnsPerRow = 3,
    responsiveColumns
) => {
    const getColumnClass = (columns) => {
        const columnMap = {
            1: "grid-cols-1",
            2: "grid-cols-2",
            3: "grid-cols-3",
            4: "grid-cols-4",
            5: "grid-cols-5",
            6: "grid-cols-6",
        }
        return columnMap[columns]
    }

    let classes = `grid ${getColumnClass(columnsPerRow)}`

    if (columnsPerRow === 1) {
        classes += " divide-y divide-gray-200"
    } else {
        classes += " divide-y md:divide-y-0 md:divide-x divide-gray-200"
    }

    if (responsiveColumns) {
        if (responsiveColumns.md) classes = classes.replace("md:grid-cols-3", `md:grid-cols-${responsiveColumns.md}`)
        if (responsiveColumns.lg) classes = classes.replace("lg:grid-cols-4", `lg:grid-cols-${responsiveColumns.lg}`)
        if (responsiveColumns.sm) classes = classes.replace("sm:grid-cols-4", `sm:grid-cols-${responsiveColumns.sm}`)
        if (responsiveColumns.xs) classes = classes.replace("xs:grid-cols-4", `xs:grid-cols-${responsiveColumns.xs}`)
    }

    return classes
}

/**
 * 回傳響應式尺寸類別
 * @param {object} [responsiveSize] - 響應式尺寸設定
 */
export const getResponsiveClasses = (responsiveSize) => {
    if (!responsiveSize) return ""

    const classes = []

    if (responsiveSize.xs) classes.push(`xs:${getSizeClasses(responsiveSize.xs)}`)
    if (responsiveSize.sm) classes.push(`sm:${getSizeClasses(responsiveSize.sm)}`)
    if (responsiveSize.md) classes.push(`md:${getSizeClasses(responsiveSize.md)}`)
    if (responsiveSize.lg) classes.push(`lg:${getSizeClasses(responsiveSize.lg)}`)
    if (responsiveSize.xl) classes.push(`xl:${getSizeClasses(responsiveSize.xl)}`)

    return classes.join(" ")
}
